import React, { useState, useEffect } from 'react';
import { useBiometricAuth } from '../hooks/useBiometricAuth';
import './BiometricSetup.css';

interface BiometricSetupProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const BiometricSetup: React.FC<BiometricSetupProps> = ({ isOpen, onClose, onSuccess }) => {
  const { 
    isAvailable, 
    hasCredentials, 
    devices, 
    isLoading, 
    registerBiometric, 
    removeDevice 
  } = useBiometricAuth();
  
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deviceName, setDeviceName] = useState('');
  const [debugLogs, setDebugLogs] = useState<string[]>([]);

  // Bloquear scroll del body cuando el modal está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'unset';
      document.body.style.width = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'unset';
      document.body.style.width = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const addLog = (message: string) => {
    setDebugLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const handleSetupBiometric = async () => {
    setDebugLogs([]); // Limpiar logs anteriores
    addLog('Iniciando configuración biométrica...');
    
    if (!isAvailable) {
      const errorMsg = 'Autenticación biométrica no disponible';
      setError(errorMsg);
      addLog(`ERROR: ${errorMsg}`);
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      addLog('Llamando a registerBiometric...');
      
      await registerBiometric(deviceName.trim() || undefined);
      
      addLog('Registro completado exitosamente');
      setSuccess('¡Autenticación biométrica configurada correctamente!');
      setDeviceName('');
      
      setTimeout(() => {
        setSuccess(null);
        onSuccess?.();
      }, 2000);
    } catch (err: any) {
      const errorMsg = err.message || 'Error al configurar autenticación biométrica';
      addLog(`ERROR: ${errorMsg}`);
      setError(errorMsg);
    }
  };

  const handleRemoveDevice = async (deviceId: number) => {
    try {
      setError(null);
      await removeDevice(deviceId);
      setSuccess('Dispositivo eliminado correctamente');
      
      setTimeout(() => setSuccess(null), 2000);
    } catch (err: any) {
      setError(err.message || 'Error al eliminar dispositivo');
    }
  };

  const getBiometricIcon = () => {
    const userAgent = navigator.userAgent;
    
    if (/iPhone|iPad|iPod/.test(userAgent)) {
      return '🔒'; // Touch ID / Face ID
    } else if (/Android/.test(userAgent)) {
      return '👆'; // Huella digital
    } else if (/Windows/.test(userAgent)) {
      return '🔐'; // Windows Hello
    } else if (/Mac/.test(userAgent)) {
      return '🔒'; // Touch ID
    } else {
      return '🔐'; // Genérico
    }
  };

  const getBiometricName = () => {
    const userAgent = navigator.userAgent;
    
    if (/iPhone|iPad|iPod/.test(userAgent)) {
      return 'Touch ID / Face ID';
    } else if (/Android/.test(userAgent)) {
      return 'Huella digital';
    } else if (/Windows/.test(userAgent)) {
      return 'Windows Hello';
    } else if (/Mac/.test(userAgent)) {
      return 'Touch ID';
    } else {
      return 'Autenticación biométrica';
    }
  };

  return (
    <div className="biometric-setup-overlay">
      <div className="biometric-setup-modal">
        <div className="biometric-setup-header">
          <h2>
            {getBiometricIcon()} Configurar {getBiometricName()}
          </h2>
          <button 
            className="biometric-setup-close"
            onClick={onClose}
            disabled={isLoading}
          >
            ×
          </button>
        </div>

        <div className="biometric-setup-content">
          {!isAvailable ? (
            <div className="biometric-setup-error">
              <p>⚠️ La autenticación biométrica no está disponible:</p>
              <ul>
                <li>Tu dispositivo no soporta esta función</li>
                <li>O necesitas estar en la versión PWA instalada</li>
                <li>O tu navegador no es compatible</li>
              </ul>
            </div>
          ) : (
            <>
              <div className="biometric-setup-info">
                <p>
                  Configura {getBiometricName()} para acceder más rápido a tu cuenta 
                  sin necesidad de introducir tu contraseña.
                </p>
                
                <div className="biometric-setup-benefits">
                  <h4>Beneficios:</h4>
                  <ul>
                    <li>✅ Acceso instantáneo y seguro</li>
                    <li>✅ No necesitas recordar contraseñas</li>
                    <li>✅ Tus datos biométricos nunca salen de tu dispositivo</li>
                    <li>✅ Funciona sin conexión a internet</li>
                  </ul>
                </div>
              </div>

              {!hasCredentials ? (
                <div className="biometric-setup-form">
                  <div className="form-group">
                    <label htmlFor="deviceName">
                      Nombre del dispositivo (opcional):
                    </label>
                    <input
                      id="deviceName"
                      type="text"
                      value={deviceName}
                      onChange={(e) => setDeviceName(e.target.value)}
                      placeholder="Mi iPhone, Laptop personal, etc."
                      maxLength={50}
                      disabled={isLoading}
                    />
                  </div>

                  <button
                    className="btn-biometric-setup"
                    onClick={handleSetupBiometric}
                    disabled={isLoading}
                  >
                    {isLoading ? '⏳ Configurando...' : `🔐 Configurar ${getBiometricName()}`}
                  </button>
                </div>
              ) : (
                <div className="biometric-devices">
                  <h4>Dispositivos configurados:</h4>
                  <div className="devices-list">
                    {devices.map((device) => (
                      <div key={device.id} className="device-item">
                        <div className="device-info">
                          <strong>{device.device_name}</strong>
                          <small>
                            Configurado: {new Date(device.created_at).toLocaleDateString()}
                            {device.last_used_at && (
                              <span> • Último uso: {new Date(device.last_used_at).toLocaleDateString()}</span>
                            )}
                          </small>
                        </div>
                        <button
                          className="btn-remove-device"
                          onClick={() => handleRemoveDevice(device.id)}
                          disabled={isLoading}
                        >
                          🗑️
                        </button>
                      </div>
                    ))}
                  </div>

                  {devices.length < 3 && (
                    <div className="add-device">
                      <div className="form-group">
                        <label htmlFor="newDeviceName">
                          Agregar otro dispositivo:
                        </label>
                        <input
                          id="newDeviceName"
                          type="text"
                          value={deviceName}
                          onChange={(e) => setDeviceName(e.target.value)}
                          placeholder="Nombre del nuevo dispositivo"
                          maxLength={50}
                          disabled={isLoading}
                        />
                      </div>

                      <button
                        className="btn-add-device"
                        onClick={handleSetupBiometric}
                        disabled={isLoading}
                      >
                        {isLoading ? '⏳ Agregando...' : '➕ Agregar dispositivo'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="biometric-setup-error">
                  <p>❌ {error}</p>
                </div>
              )}

              {success && (
                <div className="biometric-setup-success">
                  <p>✅ {success}</p>
                </div>
              )}

              {debugLogs.length > 0 && (
                <div className="biometric-debug-logs">
                  <h5>🔍 Debug Info:</h5>
                  <div className="debug-log-container">
                    {debugLogs.map((log, index) => (
                      <div key={index} className="debug-log-item">
                        {log}
                      </div>
                    ))}
                  </div>
                  <button 
                    className="btn-clear-logs"
                    onClick={() => setDebugLogs([])}
                  >
                    Limpiar logs
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="biometric-setup-footer">
          <button 
            className="btn-secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            {hasCredentials ? 'Cerrar' : 'Más tarde'}
          </button>
        </div>
      </div>
    </div>
  );
};
export default BiometricSetup;